import { type QueryStatus } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { RiskSeverity } from '@/types';

export interface RiskCountsResult {
  [RiskSeverity.Critical]: number;
  [RiskSeverity.High]: number;
  [RiskSeverity.Medium]: number;
  [RiskSeverity.Low]: number;
  [RiskSeverity.Info]: number;
  total: number;
}

export function useCustomerRiskCounts() {
  // Demonstrated (Opened) risks
  const demonstratedQuery = useMy(
    {
      resource: 'statistic',
      query: 'my#status:O#now',
      desc: true,
      allTenants: true,
    },
    {
      doNotImpersonate: true,
    }
  );

  // Resolved (Remediated) risks
  const resolvedQuery = useMy(
    {
      resource: 'statistic',
      query: 'my#status:R#now',
      desc: true,
      allTenants: true,
    },
    {
      doNotImpersonate: true,
    }
  );

  const dataMap = useMemo(() => {
    const map = new Map<string, number>();

    // Sum demonstrated + resolved counts for each username#severity key
    for (const item of [
      ...(demonstratedQuery.data ?? []),
      ...(resolvedQuery.data ?? []),
    ]) {
      const key = `${item.username}#${item.value}`;
      map.set(key, (map.get(key) ?? 0) + (item?.data?.count || 0));
    }

    return map;
  }, [demonstratedQuery.data, resolvedQuery.data]);

  const status: QueryStatus =
    demonstratedQuery.status === 'pending' || resolvedQuery.status === 'pending'
      ? 'pending'
      : demonstratedQuery.status === 'error' || resolvedQuery.status === 'error'
        ? 'error'
        : 'success';

  return {
    ...demonstratedQuery,
    status,
    dataMap,
  };
}
